import React, { useEffect, useState } from 'react';
import api, { AllCommunication } from '../api';
import { parseISO, format } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FullPageSpinner from './FullPageSpinner';
import { FaChevronRight } from 'react-icons/fa';
import Header from './Header';
import Badge from './Badge';
import { IncompleteInterview } from '../models/Interview';

const Communications = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [sharedCount, setSharedCount] = useState(0);
  const [allCommunications, setAllCommunications] = useState<AllCommunication>({ communications: [], sharedCommunications: [] });
  const [incompleteInterviews, setIncompleteInterviews] = useState<IncompleteInterview[]>([]);

  // Initialize isShared from URL params, defaulting to false if not present
  const [type, setType] = useState<string>(() => {
    return searchParams.get('type') || 'mine';
  });

  useEffect(() => {
    const fetchShareCount = async () => {
      const shareCount = await api.getCommunicationShareCounts();
      setSharedCount(shareCount.shareCount);
    };
    fetchShareCount();
  }, []);

  useEffect(() => {
    const fetchCommunications = async () => {
      const [all, incomplete] = await Promise.all([api.fetchCommunications(), api.getIncompleteInterviews()]);
      setAllCommunications(all);
      setIncompleteInterviews(incomplete);
      setLoading(false);
    };

    fetchCommunications();
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('type', type);
    setSearchParams(newSearchParams);
  }, [type, setSearchParams, searchParams]);

  if (loading) {
    return <FullPageSpinner />
  }

  const communications = type === 'shared' ? allCommunications.sharedCommunications : allCommunications.communications;

  return (
    <div className="flex flex-col items-center min-h-screen bg-white">
      <div className="w-full max-w-3xl bg-white">
        <Header title="Feedback" onBack={() => { navigate('/') }} />

        {/* Tab Menu */}
        <div className="flex border-b border-gray-300">
          <button
            className={`flex-1 py-2 px-4 font-medium ${type === 'mine' ? 'text-bespokePurple border-b-2 border-bespokePurple' : 'text-gray-500'}`}
            onClick={() => setType('mine')}
          >
            Given by Me
          </button>
          <button
            className={`flex-1 py-2 px-4 font-medium ${type === 'shared' ? 'text-bespokePurple border-b-2 border-bespokePurple' : 'text-gray-500'}`}
            onClick={() => setType('shared')}
          >
            Shared with Me
            <Badge count={sharedCount} />
          </button>
          <button
            className={`flex-1 py-2 px-4 font-medium ${type === 'incomplete' ? 'text-bespokePurple border-b-2 border-bespokePurple' : 'text-gray-500'}`}
            onClick={() => setType('incomplete')}
          >
            Unfinished
          </button>
        </div>

        <div className="flex flex-col divide-y divide-gray-300">
          {type === 'incomplete' && incompleteInterviews.map((interview, index) => (
            <div
              key={index}
              className="flex justify-between items-center p-4 hover:bg-gray-100 cursor-pointer"
              onClick={() => navigate(`/interview?interviewId=${interview.id}${interview.status ? '&regenerate=true' : ''}`)}
            >
              <div className="ml-2">
                <p className="m-1 font-semibold text-gray-800">{interview.recipientName ? `${interview.recipientName} | ` : ''} {interview.title}</p>
                <p className={`text-sm text-gray-500`}>
                  {format(parseISO(interview.lastUpdated), 'dd MMM yyyy')}
                </p>
              </div>
              <FaChevronRight className="text-gray-500" />
            </div>
          ))}
          {type !== 'incomplete' && communications.map((communication, index) => (
            <div
              key={index}
              className="flex justify-between items-center p-4 hover:bg-gray-100 cursor-pointer"
              onClick={() => navigate(`/communication/${communication.id}`)}
            >
              <div className="ml-2">
                <p className="m-1 font-semibold text-gray-800">{communication.otherParticipant ? `${communication.otherParticipant} | ` : ''} {communication.title}</p>
                <p className={`text-sm text-gray-500 ${type === 'shared' && !communication.viewed ? 'font-bold' : ''}`}>
                  {`${format(parseISO(communication.created), 'dd MMM yyyy')}`}
                </p>
                <p className={`italic text-sm text-gray-500 ${type === 'shared' && !communication.viewed ? 'font-bold' : ''}`}>
                  {`${communication.text.substring(0, 100)}...`}
                </p>
              </div>
              <FaChevronRight className="text-gray-500" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Communications;